import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function AuthenticRamenInVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "authentic-ramen-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Ramen Danbo",
    "section-2": "Hokkaido Ramen Santouka",
    "section-3": "The Ramen Butcher",
    "section-4": "Jinya Ramen Bar",
    "section-5": "Maruhachi Ra-men",
    "section-6": "Kintaro Ramen",
    "section-7": "Ramen Tsukiya",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  // Each item in referencesData can be a link or plain text. 
  // The Bottom component will format them as a references list.
  const referencesData = [
    {
      url: "https://ramendanbo.ca/",
      label: "Ramen Danbo Official Website",
    },
    {
      url: "https://www.santouka.co.jp/en/",
      label: "Hokkaido Ramen Santouka",
    },
    {
      url: "https://jinya-ramenbar.com/",
      label: "Jinya Ramen Bar Official Site",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    {
      id: "section-1",
      lat: 49.2687,
      lng: -123.1463,
      popupText: "Ramen Danbo (Kitsilano)",
    },
    {
      id: "section-2",
      lat: 49.2902,
      lng: -123.1338,
      popupText: "Hokkaido Ramen Santouka (Robson)",
    },
    {
      id: "section-3",
      lat: 49.2795,
      lng: -123.0985,
      popupText: "The Ramen Butcher (Chinatown)",
    },
    {
      id: "section-4",
      lat: 49.2796,
      lng: -123.1193,
      popupText: "Jinya Ramen Bar (Downtown)",
    },
    {
      id: "section-5",
      lat: 49.1841,
      lng: -123.1335,
      popupText: "Maruhachi Ra-men (Richmond)",
    },
    {
      id: "section-6",
      lat: 49.2895,
      lng: -123.1390,
      popupText: "Kintaro Ramen (West End)",
    },
    {
      id: "section-7",
      lat: 49.2890,
      lng: -123.1395,
      popupText: "Ramen Tsukiya (West End)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Where to Find Authentic Ramen in Vancouver</title>
        <meta
          name="description"
          content="A comprehensive guide to Vancouver's best ramen spots, featuring tonkotsu, shoyu, miso, vegetarian options, and more."
        />
        <meta
          name="keywords"
          content="ramen, Vancouver, tonkotsu, shoyu, miso, vegetarian ramen"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/authentic-ramen-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Where to Find Authentic Ramen in Vancouver"
        />
        <meta
          property="og:description"
          content="A comprehensive guide to Vancouver's best ramen spots, with tips on popular bowls, customization, and local dining practices."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/authentic-ramen-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/authentic-ramen-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Where to Find Authentic Ramen in Vancouver"
        />
        <meta
          name="twitter:description"
          content="Explore Vancouver's top ramen shops for rich tonkotsu, light shoyu, hearty miso, and flavorful vegetarian bowls."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/authentic-ramen-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Bowl of ramen noodles with toppings"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s ramen scene is vibrant and varied – from rich tonkotsu
              broths to delicate shoyu soups, hearty miso bowls, and even
              satisfying vegetarian options. This guide highlights some of the
              best places to slurp authentic ramen in Vancouver, spanning
              neighborhoods like Downtown, Richmond, and Kitsilano. We’ve
              included a mix of budget-friendly shops and high-end spots so you
              can enjoy great ramen no matter your budget. Read on for top
              restaurants (with addresses, hours, prices, and unique features),
              local dining tips, an FAQ, and more.
            </p>

            {/* SECTION 1: Ramen Danbo */}
            <h2 id="section-1">Ramen Danbo</h2>
            <p>
              Ramen Danbo is an import from Fukuoka, specializing in
              Hakata-style tonkotsu ramen. They let you customize nearly every
              aspect of your bowl – from noodle firmness to broth richness and
              spice level – for a personalized experience. Long lines during
              peak hours reflect its popularity, but the wait is worth it for
              their creamy pork-based soup.
            </p>
            <p
              ref={(el) => setSpotRef("section-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Ramen Danbo (Kitsilano)</strong>{" "}
              <Marker
                coordY={49.2687}
                coordX={-123.1463}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 1833 West 4th Avenue, Vancouver, BC V6J 1M4. Operating hours:
              11:00 AM – 11:00 PM daily. Price range: $13–$15 per bowl. Key
              feature: fully customizable Hakata ramen.
            </p>

            {/* SECTION 2: Hokkaido Ramen Santouka */}
            <h2 id="section-2">Hokkaido Ramen Santouka</h2>
            <p>
              A famous chain from Hokkaido, Santouka is best known for its mild
              and creamy shio (salt) broth. The soup is simmered for hours to
              achieve a gentle yet rich flavor profile. Their toroniku (pork
              cheek) is notably tender and sought-after by ramen aficionados.
            </p>
            <p
              ref={(el) => setSpotRef("section-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Hokkaido Ramen Santouka (Robson)</strong>{" "}
              <Marker
                coordY={49.2902}
                coordX={-123.1338}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 1690 Robson Street, Vancouver, BC V6G 1C7. Open 11:00 AM – 10:00
              PM (Sun–Thu) and until 11:00 PM (Fri–Sat). Price range: $14–$18 per
              bowl. Specialty: Hokkaido-style shio ramen.
            </p>

            {/* SECTION 3: The Ramen Butcher */}
            <h2 id="section-3">The Ramen Butcher</h2>
            <p>
              Situated in Chinatown, The Ramen Butcher serves Hakata-style
              tonkotsu with options like black garlic and green basil-infused
              ramen. They also offer free noodle refills by simply requesting
              “kaedama.” The atmosphere is casual and wallet-friendly.
            </p>
            <p
              ref={(el) => setSpotRef("section-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>The Ramen Butcher (Chinatown)</strong>{" "}
              <Marker
                coordY={49.2795}
                coordX={-123.0985}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 223 E Georgia Street, Vancouver, BC V6A 1Z6. Open 11:30 AM – 10:00
              PM daily. Price range: $11–$14 per bowl. Don’t miss their vegetarian
              coconut curry ramen as well.
            </p>

            {/* SECTION 4: Jinya Ramen Bar */}
            <h2 id="section-4">Jinya Ramen Bar</h2>
            <p>
              A modern ramen bar with multiple locations, Jinya prides itself on
              long-simmered broths for deep flavor. They serve everything from
              classic tonkotsu to creative vegan bowls like their Creamy Spicy
              Vegan Ramen. The sleek décor and full bar make it perfect for a
              social evening.
            </p>
            <p
              ref={(el) => setSpotRef("section-4", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Jinya Ramen Bar (Downtown)</strong>{" "}
              <Marker
                coordY={49.2796}
                coordX={-123.1193}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 541 Robson Street, Vancouver, BC V6B 2B7. Hours: 11:00 AM – 10:00
              PM (Sun–Thu), 11:00 AM – 11:00 PM (Fri–Sat). Price range: $15–$18
              per bowl. Notable for vegan-friendly options.
            </p>

            {/* SECTION 5: Maruhachi Ra-men */}
            <h2 id="section-5">Maruhachi Ra-men</h2>
            <p>
              Formerly known as Marutama in some areas, Maruhachi specializes in
              a chicken-based paitan broth. The lighter soup still packs plenty
              of umami, especially their popular Tamago Ramen with a perfectly
              soft-boiled egg. Affordable, family-friendly, and a hit with
              students.
            </p>
            <p
              ref={(el) => setSpotRef("section-5", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Maruhachi Ra-men (Richmond)</strong>{" "}
              <Marker
                coordY={49.1841}
                coordX={-123.1335}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 2780 No. 3 Road, Richmond, BC V6X 0A4. Open 11:30 AM – 9:30 PM
              daily. Price range: $12–$15 per bowl. Try the crispy garlic chips
              for an extra flavor boost.
            </p>

            {/* SECTION 6: Kintaro Ramen */}
            <h2 id="section-6">Kintaro Ramen</h2>
            <p>
              One of Vancouver’s original ramen shops, Kintaro is known for its
              classic miso ramen. The open kitchen, communal seating, and lineups
              at lunch evoke an authentic Tokyo vibe. Portions are generous, and
              you can customize richness and fat level to suit your taste.
            </p>
            <p
              ref={(el) => setSpotRef("section-6", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Kintaro Ramen (West End)</strong>{" "}
              <Marker
                coordY={49.2895}
                coordX={-123.1390}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 788 Denman Street, Vancouver, BC V6G 2L5. Hours: 11:30 AM – 9:00
              PM (Tue–Thu, Sun); 11:30 AM – 9:30 PM (Fri–Sat); closed Mondays.
              Price range: $11–$14. Cash or debit only, so plan accordingly.
            </p>

            {/* SECTION 7: Ramen Tsukiya */}
            <h2 id="section-7">Ramen Tsukiya</h2>
            <p>
              This modern spot near Stanley Park has gained attention for its
              Bamboo Charcoal Dark Miso Ramen – a jet-black, garlicky broth that
             ’s great for adventurous eaters. They also offer tsukemen and cold
              ramen options for hotter days.
            </p>
            <p
              ref={(el) => setSpotRef("section-7", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Ramen Tsukiya (West End)</strong>{" "}
              <Marker
                coordY={49.2890}
                coordX={-123.1395}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 740 Denman Street, Vancouver, BC V6G 2L5. Open 11:30 AM – 2:30 PM,
              5:00 PM – 9:00 PM daily. Price range: $14–$17. Try the black miso
              for a unique twist on classic ramen.
            </p>

            {/* FAQ SECTION */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Is ramen expensive in Vancouver?</h3>
              <p>
                Not really – you can find bowls around $11-$12 for a basic option
                at places like The Ramen Butcher or Kintaro, while more elaborate
                bowls can cost $15-$18 at trendier spots. Generally, it’s a good
                value meal.
              </p>

              <h3>2. Can I find vegetarian or vegan ramen here?</h3>
              <p>
                Yes! Many shops have vegan or vegetarian choices. Jinya offers a
                range of plant-based options, Danbo has a vegan broth, and The
                Ramen Butcher does a vegetarian curry ramen. Vancouver is very
                veg-friendly overall.
              </p>

              <h3>3. Do I need a reservation?</h3>
              <p>
                Most ramen places operate on a walk-in basis and do not take
                reservations. Expect a queue at peak hours. Quick turnover means
                you typically won’t wait too long.
              </p>
            </div>

            {/* LOCAL REGULATIONS */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              All restaurants in Vancouver follow strict food safety standards.
              Smoking is banned indoors in BC. Tipping 15–20% is customary for
              dine-in service. Always check signage for any specific COVID-19
              related measures or capacity limits (if in effect). Some smaller
              shops are cash-only. 
            </p>

            {/* CONCLUSION */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s ramen scene is as diverse as the city itself. From
              silky pork bone broths to creative miso variations, there’s a bowl
              for every palate. Follow these recommendations to explore the best
              of local ramen, and don’t hesitate to slurp loudly – it’s a badge
              of authenticity!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}